/**
 * Today — the single-focus morning dashboard.
 *
 * ADHD-friendly by design: one screen, one scroll, biggest-first ordering
 * (overdue → today → next up → unread messages → the day's numbers).
 * Everything is one tap away from action; no filters, no configuration.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Calendar, CheckCircle2, Circle, Clock, MessageSquare, ArrowRight,
  CalendarDays, Flame, Sparkles, MapPin, Phone as PhoneIcon, User,
} from "lucide-react";
import { DashboardSkeleton } from "../components/skeletons";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function inNextNDaysISO(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function isToday(dateStr) {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) === todayISO();
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) < todayISO();
}

function fmtTime(str) {
  if (!str) return "";
  // Accepts "HH:MM" or an ISO string.
  const t = str.length <= 5 ? str : new Date(str).toTimeString().slice(0, 5);
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  const disp = ((hh + 11) % 12) + 1;
  return `${disp}:${m} ${ampm}`;
}

function greetingFor(name) {
  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  return name ? `${greet}, ${name.split(" ")[0]}` : greet;
}

export default function Today() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [unreadSms, setUnreadSms] = useState(0);
  const [busyTaskId, setBusyTaskId] = useState(null);

  const loadAll = async () => {
    try {
      const [tRes, eRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/tasks?status=pending`, { headers: auth() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/schedule?start_date=${todayISO()}&end_date=${inNextNDaysISO(7)}`, { headers: auth() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/sms/unread-count`, { headers: auth() }).catch(() => ({ data: { count: 0 } })),
      ]);
      setTasks(tRes.data || []);
      setEvents(eRes.data || []);
      setUnreadSms(sRes.data?.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const overdueTasks = useMemo(
    () => tasks.filter((t) => t.status !== "completed" && isOverdue(t.due_date)),
    [tasks],
  );
  const todayTasks = useMemo(
    () => tasks.filter((t) => t.status !== "completed" && isToday(t.due_date)),
    [tasks],
  );
  const nextTasks = useMemo(
    () => tasks
      .filter((t) => t.status !== "completed" && t.due_date && t.due_date.slice(0, 10) > todayISO())
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
      .slice(0, 3),
    [tasks],
  );
  const todayEvents = useMemo(
    () => events
      .filter((e) => isToday(e.start_date))
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")),
    [events],
  );
  const upcomingEvents = useMemo(
    () => events
      .filter((e) => e.start_date && e.start_date.slice(0, 10) > todayISO() && e.start_date.slice(0, 10) <= inNextNDaysISO(3))
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))
      .slice(0, 4),
    [events],
  );

  const toggleTask = async (task, e) => {
    e?.stopPropagation();
    if (busyTaskId) return;
    setBusyTaskId(task.id);
    const nextStatus = task.status === "completed" ? "pending" : "completed";
    try {
      await axios.patch(`${API_URL}/tasks/${task.id}/status?status=${nextStatus}`, {}, { headers: auth() });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
      if (nextStatus === "completed") toast.success("Nice — one down.");
    } catch {
      toast.error("Couldn't update task");
    } finally {
      setBusyTaskId(null);
    }
  };

  if (loading) {
    return (
      <div>
        <Header title="Today" />
        <div className="p-4 sm:p-6">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  const nothingLeft =
    overdueTasks.length === 0 &&
    todayTasks.length === 0 &&
    todayEvents.length === 0 &&
    unreadSms === 0;

  return (
    <div data-testid="today-page">
      <Header title="Today" />
      <div
        className="p-4 sm:p-6 space-y-6 max-w-3xl mx-auto"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
      >
        {/* Hero */}
        <div>
          <p className="text-sm text-slate-500" data-testid="today-date">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </p>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mt-1">
            {greetingFor(user?.name)}
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            {overdueTasks.length > 0
              ? `${overdueTasks.length} overdue — knock those out first.`
              : todayTasks.length > 0
              ? `${todayTasks.length} task${todayTasks.length === 1 ? "" : "s"} due today.`
              : todayEvents.length > 0
              ? `${todayEvents.length} appointment${todayEvents.length === 1 ? "" : "s"} on the calendar.`
              : "You're clear. Nice."}
          </p>
        </div>

        {/* At-a-glance strip */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <MetricPill
            testid="metric-overdue"
            label="Overdue"
            value={overdueTasks.length}
            tone={overdueTasks.length > 0 ? "warn" : "muted"}
            icon={Flame}
            onClick={() => navigate("/tasks")}
          />
          <MetricPill
            testid="metric-today"
            label="Today"
            value={todayTasks.length + todayEvents.length}
            tone="brand"
            icon={Sparkles}
            onClick={() => navigate("/tasks")}
          />
          <MetricPill
            testid="metric-unread-sms"
            label="Unread"
            value={unreadSms}
            tone={unreadSms > 0 ? "brand" : "muted"}
            icon={MessageSquare}
            onClick={() => navigate("/inbox")}
          />
        </div>

        {nothingLeft && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="today-empty-state">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold text-emerald-900">Nothing on the list.</p>
            <p className="text-sm text-emerald-700 mt-1">Take the win — or line up tomorrow.</p>
          </div>
        )}

        {/* Overdue */}
        {overdueTasks.length > 0 && (
          <Section
            testid="section-overdue"
            title={`Overdue (${overdueTasks.length})`}
            accent="amber"
            action={<Link to="/tasks" className="text-xs text-slate-500 hover:text-slate-700">See all →</Link>}
          >
            <div className="space-y-2">
              {overdueTasks.slice(0, 5).map((t) => (
                <TaskRow key={t.id} task={t} onToggle={toggleTask} overdue />
              ))}
              {overdueTasks.length > 5 && (
                <button
                  onClick={() => navigate("/tasks")}
                  className="text-xs text-slate-500 hover:text-slate-700 pl-9"
                  data-testid="today-more-overdue"
                >
                  + {overdueTasks.length - 5} more overdue
                </button>
              )}
            </div>
          </Section>
        )}

        {/* Today's tasks */}
        {todayTasks.length > 0 && (
          <Section
            testid="section-today-tasks"
            title="Tasks due today"
            action={<Link to="/tasks" className="text-xs text-slate-500 hover:text-slate-700">See all →</Link>}
          >
            <div className="space-y-2">
              {todayTasks.map((t) => (
                <TaskRow key={t.id} task={t} onToggle={toggleTask} />
              ))}
            </div>
          </Section>
        )}

        {/* Today's appointments */}
        {todayEvents.length > 0 && (
          <Section
            testid="section-today-events"
            title="On the calendar"
            action={<Link to="/schedule" className="text-xs text-slate-500 hover:text-slate-700">Schedule →</Link>}
          >
            <div className="space-y-2">
              {todayEvents.map((e) => <EventRow key={e.id} event={e} onClick={() => e.project_id && navigate(`/projects/${e.project_id}`)} />)}
            </div>
          </Section>
        )}

        {/* Unread SMS shortcut */}
        {unreadSms > 0 && (
          <Section testid="section-unread-sms" title="You've got messages">
            <button
              onClick={() => navigate("/inbox")}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
              data-testid="today-open-inbox"
            >
              <div className="w-10 h-10 rounded-full bg-[#0F4C81]/10 text-[#0F4C81] flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {unreadSms} unread text{unreadSms === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-slate-500">Open the Inbox to reply</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
          </Section>
        )}

        {/* Next up (later this week) */}
        {(upcomingEvents.length > 0 || nextTasks.length > 0) && (
          <Section
            testid="section-next-up"
            title="Next up"
            action={<Link to="/schedule" className="text-xs text-slate-500 hover:text-slate-700">Week →</Link>}
          >
            <div className="space-y-2">
              {upcomingEvents.map((e) => (
                <EventRow key={e.id} event={e} showDate onClick={() => e.project_id && navigate(`/projects/${e.project_id}`)} />
              ))}
              {nextTasks.map((t) => (
                <TaskRow key={t.id} task={t} onToggle={toggleTask} showDate />
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function MetricPill({ label, value, tone = "muted", icon: Icon, onClick, testid }) {
  const tones = {
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    brand: "bg-[#0F4C81]/8 border-[#0F4C81]/20 text-[#0F4C81]",
    muted: "bg-slate-50 border-slate-200 text-slate-600",
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-xl border px-3 py-3 text-left transition-colors hover:shadow-sm min-h-[64px] ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold mt-1 leading-none">{value}</div>
    </button>
  );
}

function Section({ title, children, action, accent, testid }) {
  const accentBar =
    accent === "amber" ? "before:bg-amber-500" : "before:bg-[#0F4C81]";
  return (
    <section data-testid={testid}>
      <div
        className={`flex items-center justify-between mb-2 relative pl-3 before:content-[''] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full ${accentBar}`}
      >
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function TaskRow({ task, onToggle, overdue = false, showDate = false }) {
  const priorityColor =
    task.priority === "high" ? "bg-rose-100 text-rose-700" :
    task.priority === "medium" ? "bg-amber-100 text-amber-700" :
    "bg-slate-100 text-slate-600";
  return (
    <div
      className={`bg-white rounded-xl border p-3 sm:p-4 flex items-center gap-3 ${
        overdue ? "border-amber-200" : "border-slate-200"
      }`}
      data-testid={`today-task-${task.id}`}
    >
      <button
        type="button"
        onClick={(e) => onToggle(task, e)}
        className="flex-shrink-0 h-11 w-11 -m-2 flex items-center justify-center"
        aria-label="Complete task"
        data-testid={`today-toggle-${task.id}`}
      >
        {task.status === "completed" ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
        ) : (
          <Circle className="w-6 h-6 text-slate-300" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
          {task.priority && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${priorityColor}`}>
              {task.priority}
            </span>
          )}
          {(overdue || showDate) && task.due_date && (
            <span className={`inline-flex items-center gap-1 ${overdue ? "text-amber-700" : ""}`}>
              <Clock className="w-3 h-3" />
              {new Date(task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function EventRow({ event, showDate = false, onClick }) {
  const contactLine = event.contact_name || event.customer_name || event.title || "Appointment";
  const address = event.address || event.location || "";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
      data-testid={`today-event-${event.id}`}
    >
      <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#0F4C81]/10 text-[#0F4C81] flex flex-col items-center justify-center">
        <CalendarDays className="w-4 h-4" />
        <span className="text-[10px] font-bold leading-none mt-0.5">
          {event.start_time ? fmtTime(event.start_time).split(" ")[0] : "—"}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{contactLine}</p>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
          {showDate && event.start_date && (
            <span>{new Date(event.start_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
          )}
          {event.start_time && <span>{fmtTime(event.start_time)}</span>}
          {address && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{address}</span>
            </span>
          )}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}
